import { type FC, useEffect } from "react";

import {
  FormField,
  Spinner,
  IconButton,
  Row,
  RefreshIcon,
  Combobox,
} from "@hightouch/htui";
import * as Yup from "yup";

import { useMetabaseListQuestionsQuery } from "src/graphql";

import type {
  CustomQueryViewProps,
  CustomQueryFormProps,
} from "../custom-query";

export const QueryForm: FC<Readonly<CustomQueryFormProps>> = ({
  source,
  query,
  onChange,
  setError,
}) => {
  const {
    data: questions,
    isLoading: questionsLoading,
    error: questionsError,
    refetch: listQuestions,
  } = useMetabaseListQuestionsQuery({ connectionId: String(source.id) });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setError(questionsError);
  }, [questionsError]);

  return (
    <FormField label="Question" error={questionsError?.message}>
      <Row gap={2}>
        <Combobox
          isLoading={questionsLoading}
          options={
            questions?.metabaseListQuestions?.map((q) => ({
              label: q.name,
              value: q.id,
            })) ?? []
          }
          placeholder="Select a question…"
          value={query?.id}
          onChange={(id) => onChange({ ...query, id })}
        />
        <IconButton
          aria-label="Refresh questions"
          icon={RefreshIcon}
          isDisabled={questionsLoading}
          variant="secondary"
          onClick={() => listQuestions()}
        />
      </Row>
    </FormField>
  );
};

export const QueryView: FC<Readonly<CustomQueryViewProps>> = ({
  source,
  query,
  setError,
}) => {
  const {
    data: questions,
    isLoading: questionsLoading,
    error: questionsError,
  } = useMetabaseListQuestionsQuery({ connectionId: String(source.id) });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setError(questionsError);
  }, [questionsError]);

  const question = questions?.metabaseListQuestions?.find(
    (q) => q.id === query.id,
  );
  const questionName = question?.name || `Unknown question with id ${query.id}`;

  return (
    <FormField label="Question">
      {questionsLoading ? <Spinner /> : questionName}
    </FormField>
  );
};

export const querySchema = Yup.object().shape({
  type: Yup.string().required().equals(["question"]),
  id: Yup.number().required(),
});
